/**
 * RAE — Appointment Engine shared types.
 *
 * 5-step customer-facing booking flow: Service → Method → Details → Documents → Your details.
 * Backing routes under `/v3/bookings/*` are PLANNED (digest 03). The booking wizard
 * currently writes/reads state to an in-memory reducer so the demo flows end-to-end
 * without any server.
 */

export type ServiceCategory = 'personal' | 'business' | 'trust';

/** Narrow subset of RAE services presented on Step 1. */
export type ServiceCode =
  | 'DDA'
  | 'SAV'
  | 'CD'
  | 'IRA'
  | 'SDB'
  | 'UPDATE_INFO'
  | 'OTHER';

export interface RaeService {
  code: ServiceCode;
  category: ServiceCategory;
  title: string;
  description: string;
  /** Minutes. Shown as `● 45 min` under the title. */
  durationMin: number;
  icon: string;
}

export type MeetingMethod = 'inPerson' | 'phone' | 'video';

export interface RaeBranchChoice {
  branchCode: string;
  branchName: string;
  addressLine1: string;
  city: string;
  state: string;
  zip: string;
  distanceMiles?: number;
}

export interface RaeSlot {
  /** ISO local (America/Chicago) date, `YYYY-MM-DD`. */
  date: string;
  /** 24h HH:mm. */
  time: string;
  available: boolean;
  /** When `available === true` but capacity is tight. Must pair color + icon + text. */
  limitedAvailability?: boolean;
}

export interface UploadedFile {
  id: string;
  name: string;
  sizeBytes: number;
  /** Dev-only — in real wiring the file goes to SP via `/v3/bookings/{id}/uploads`. */
  localOnly: true;
}

export interface BookingWizardState {
  service: RaeService | null;
  method: MeetingMethod | null;
  branch: RaeBranchChoice | null;
  date: string | null;
  time: string | null;
  files: ReadonlyArray<UploadedFile>;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  smsReminders: boolean;
  fundingAmountRange: string;
  notes: string;
  /** Populated on confirmation step once server (or local fallback) issues a code. */
  confirmationNumber: string | null;
  officerDisplayName: string | null;
  officerInitials: string | null;
}

export const WIZARD_STEP_LABELS = [
  'Service',
  'Meeting Method',
  'Meeting Details',
  'Documents',
  'Your Details',
] as const;

export type WizardStepIndex = 0 | 1 | 2 | 3 | 4;

// ---------------------------------------------------------------------------
// Appointments landing — unified surface (M365 Calendar + Bookings + Teams)
// ---------------------------------------------------------------------------

/**
 * Channel of an officer-facing appointment. Drives the card chip + the
 * appropriate primary CTA (Join / Mark arrived / Call back).
 */
export type AppointmentChannel = 'inBranch' | 'phone' | 'teams' | 'bookings';

/**
 * Where the appointment originated. Calendar = manually scheduled by an
 * officer (or by another officer-facing tool), Bookings = customer
 * self-served via a public Bookings page, External = forwarded invite.
 */
export type AppointmentSource = 'calendar' | 'bookings' | 'external';

/**
 * Unified appointment shape — composed from multiple Graph endpoints.
 * Officers never see raw Graph IDs; cards display only customer-facing
 * fields (per Bill 2026-04-23 cart-card standard).
 */
export interface Appointment {
  /** Internal stable id, never displayed. */
  id: string;
  /** Customer / contact name shown on the card. */
  customerName: string;
  /** Subject / appointment type (e.g., "Open new account"). */
  subject: string;
  /** Optional service code, when discoverable from Bookings or category. */
  serviceCode?: string;
  /** ISO 8601 UTC start. */
  startUtc: string;
  /** ISO 8601 UTC end. */
  endUtc: string;
  /** Time zone label for display only. */
  timeZone: string;
  channel: AppointmentChannel;
  source: AppointmentSource;
  /** Branch this appointment belongs to (FSR's home branch by default). */
  branchCode?: string;
  branchName?: string;
  /** Display name of assigned officer; never their email. */
  assignedOfficer?: string;
  /** Initials fallback when an avatar isn't available. */
  assignedOfficerInitials?: string;
  /** Customer-supplied notes. */
  notes?: string;
  /** Teams join URL — never displayed; consumed only by JoinMeetingButton. */
  joinWebUrl?: string;
  /** Did the customer self-check-in / arrive? */
  arrived?: boolean;
  /** Cancelled in the source system. Cards still render with strikethrough. */
  cancelled?: boolean;
}

/**
 * Bookings business config — one row per branch (lives in v3 SQL `Branches`
 * table once admin Config tab seeds it). Branch.bookingsBusinessId is the
 * id from `/solutions/bookingBusinesses/{id}` Graph endpoint.
 */
export interface BookingsBusinessConfig {
  branchCode: string;
  /** Graph Bookings business id. Empty string = not yet configured. */
  bookingsBusinessId: string;
  /** Public-facing booking page URL — what we share with customers. */
  publicBookingUrl?: string;
  /** Display name from Bookings. */
  displayName?: string;
  /** Last successful sync timestamp (ISO). */
  lastSyncUtc?: string;
}

/**
 * Channel choice on the New Appointment modal.
 */
export type NewAppointmentChannel =
  | 'inBranch'
  | 'phone'
  | 'teams'
  | 'bookingsLink';

/**
 * Payload for the unified create flow. Fields the user filled in the
 * modal — the right Graph endpoint(s) get picked downstream based on
 * channel.
 */
export interface NewAppointmentInput {
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  subject: string;
  channel: NewAppointmentChannel;
  /** ISO yyyy-mm-dd. */
  date: string;
  /** 24h HH:mm. */
  time: string;
  /** Minutes. */
  durationMin: number;
  branchCode: string;
  notes?: string;
}

/**
 * Result returned from `useNewAppointment().create(input)`. Always
 * customer-friendly — never echoes a Graph id.
 */
export interface NewAppointmentResult {
  ok: boolean;
  /** Confirmation code if ok; error message otherwise. */
  message: string;
  /** When channel === 'bookingsLink', the public link to share. */
  bookingsLink?: string;
}
